/**
 * Document Generator API Service
 * Handles all document generator-related API operations
 */

import { apiConfig, makeApiRequest, ApiResponse } from './apiConfig';
import {
  DocumentTemplate,
  GeneratedDocument,
  ApprovalWorkflow,
  PendingApproval,
  TemplateFilters,
  DocumentFilters,
  WorkflowFilters,
  CreateTemplateInput,
  UpdateTemplateInput,
  GenerateDocumentInput,
  UpdateDocumentInput,
  CreateWorkflowInput,
  ApprovalActionInput,
  LockClauseInput,
  PaginatedResponse,
  PaginationInfo,
  AIImprovementResult,
  AIImprovementMode,
  AlternativeSuggestion,
  ComplianceCheckResult,
  ClauseGenerationResult,
  Jurisdiction,
  JurisdictionVariant,
  DocumentVersion
} from '../../types/documentGenerator.types';

// =============================================================================
// Types for API Responses
// =============================================================================

interface TemplateListResponse {
  data: DocumentTemplate[];
  pagination: PaginationInfo;
}

interface DocumentListResponse {
  data: GeneratedDocument[];
  pagination: PaginationInfo;
}

interface WorkflowListResponse {
  data: ApprovalWorkflow[];
  pagination: PaginationInfo;
}

interface PendingApprovalsResponse {
  data: PendingApproval[];
  pagination: PaginationInfo;
}

interface VersionCompareResult {
  version1: number;
  version2: number;
  differences: Array<{
    type: 'added' | 'removed' | 'modified';
    location: string;
    content: string;
  }>;
  summary: string;
}

interface SimplifyResult {
  simplified: string;
  keyTermsExplained: Array<{ term: string; explanation: string }>;
  readabilityScore: number;
}

interface ChangeSummaryResult {
  summary: string;
  significantChanges: string[];
  riskAssessment: string;
}

interface PlaceholderAnalysisResult {
  placeholders: Array<{
    key: string;
    suggestedLabel: string;
    suggestedType: string;
    required: boolean;
  }>;
  suggestions: string[];
}

// =============================================================================
// Template Service
// =============================================================================

class TemplateService {
  /**
   * Get all templates with optional filtering
   */
  async getAll(token: string, filters: TemplateFilters = {}): Promise<TemplateListResponse> {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.search) params.append('search', filters.search);
      if (filters.isActive !== undefined) params.append('isActive', String(filters.isActive));
      if (filters.tags?.length) params.append('tags', filters.tags.join(','));
      if (filters.page) params.append('page', String(filters.page));
      if (filters.limit) params.append('limit', String(filters.limit));
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

      const queryString = params.toString();
      const url = queryString
        ? `${apiConfig.endpoints.documentGenerator.templates.getAll}?${queryString}`
        : apiConfig.endpoints.documentGenerator.templates.getAll;

      const response = await makeApiRequest<DocumentTemplate[]>(url, { method: 'GET' }, token);

      // The API returns { success: true, data: [...templates], pagination: {...} }
      // We need to restructure it to match TemplateListResponse
      const defaultPagination = { page: 1, limit: 10, total: 0, totalPages: 0, hasNextPage: false, hasPrevPage: false };
      const pag = response.pagination as any;
      return {
        data: response.data || [],
        pagination: pag ? {
          page: pag.page || 1,
          limit: pag.limit || 10,
          total: pag.total || 0,
          totalPages: pag.totalPages || pag.pages || 0,
          hasNextPage: pag.hasNextPage || false,
          hasPrevPage: pag.hasPrevPage || false
        } : defaultPagination
      };
    } catch (error) {
      console.error('Error fetching templates:', error);
      throw error;
    }
  }

  /**
   * Get single template by ID
   */
  async getById(token: string, id: string): Promise<DocumentTemplate> {
    try {
      const response = await makeApiRequest<DocumentTemplate>(
        apiConfig.endpoints.documentGenerator.templates.getOne(id),
        { method: 'GET' },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error fetching template ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new template
   */
  async create(token: string, input: CreateTemplateInput): Promise<DocumentTemplate> {
    try {
      const response = await makeApiRequest<DocumentTemplate>(
        apiConfig.endpoints.documentGenerator.templates.create,
        {
          method: 'POST',
          body: JSON.stringify(input)
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error('Error creating template:', error);
      throw error;
    }
  }

  /**
   * Update an existing template
   */
  async update(token: string, id: string, input: UpdateTemplateInput): Promise<DocumentTemplate> {
    try {
      const response = await makeApiRequest<DocumentTemplate>(
        apiConfig.endpoints.documentGenerator.templates.update(id),
        {
          method: 'PUT',
          body: JSON.stringify(input)
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error updating template ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a template
   */
  async delete(token: string, id: string): Promise<boolean> {
    try {
      await makeApiRequest(
        apiConfig.endpoints.documentGenerator.templates.delete(id),
        { method: 'DELETE' },
        token
      );
      return true;
    } catch (error) {
      console.error(`Error deleting template ${id}:`, error);
      throw error;
    }
  }

  /**
   * Archive a template
   */
  async archive(token: string, id: string): Promise<DocumentTemplate> {
    try {
      const response = await makeApiRequest<DocumentTemplate>(
        apiConfig.endpoints.documentGenerator.templates.archive(id),
        { method: 'POST' },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error archiving template ${id}:`, error);
      throw error;
    }
  }

  /**
   * Restore an archived template
   */
  async restore(token: string, id: string): Promise<DocumentTemplate> {
    try {
      const response = await makeApiRequest<DocumentTemplate>(
        apiConfig.endpoints.documentGenerator.templates.restore(id),
        { method: 'POST' },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error restoring template ${id}:`, error);
      throw error;
    }
  }

  /**
   * Clone a template
   */
  async clone(token: string, id: string, newName?: string): Promise<DocumentTemplate> {
    try {
      const response = await makeApiRequest<DocumentTemplate>(
        apiConfig.endpoints.documentGenerator.templates.clone(id),
        {
          method: 'POST',
          body: JSON.stringify({ name: newName })
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error cloning template ${id}:`, error);
      throw error;
    }
  }

  /**
   * Lock a clause in a template
   */
  async lockClause(token: string, id: string, input: LockClauseInput): Promise<DocumentTemplate> {
    try {
      const response = await makeApiRequest<DocumentTemplate>(
        apiConfig.endpoints.documentGenerator.templates.lockClause(id),
        {
          method: 'POST',
          body: JSON.stringify(input)
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error locking clause in template ${id}:`, error);
      throw error;
    }
  }

  /**
   * Unlock a clause in a template
   */
  async unlockClause(token: string, templateId: string, clauseId: string): Promise<DocumentTemplate> {
    try {
      const response = await makeApiRequest<DocumentTemplate>(
        apiConfig.endpoints.documentGenerator.templates.unlockClause(templateId, clauseId),
        { method: 'DELETE' },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error unlocking clause ${clauseId} in template ${templateId}:`, error);
      throw error;
    }
  }

  /**
   * Add a jurisdiction variant
   */
  async addJurisdiction(token: string, templateId: string, variant: Omit<JurisdictionVariant, 'jurisdictionCode'> & { jurisdictionCode: string }): Promise<DocumentTemplate> {
    try {
      const response = await makeApiRequest<DocumentTemplate>(
        apiConfig.endpoints.documentGenerator.templates.addJurisdiction(templateId),
        {
          method: 'POST',
          body: JSON.stringify(variant)
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error adding jurisdiction to template ${templateId}:`, error);
      throw error;
    }
  }

  /**
   * Update a jurisdiction variant
   */
  async updateJurisdiction(token: string, templateId: string, jurisdictionCode: string, variant: Partial<JurisdictionVariant>): Promise<DocumentTemplate> {
    try {
      const response = await makeApiRequest<DocumentTemplate>(
        apiConfig.endpoints.documentGenerator.templates.updateJurisdiction(templateId, jurisdictionCode),
        {
          method: 'PUT',
          body: JSON.stringify(variant)
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error updating jurisdiction ${jurisdictionCode} in template ${templateId}:`, error);
      throw error;
    }
  }

  /**
   * Remove a jurisdiction variant
   */
  async removeJurisdiction(token: string, templateId: string, jurisdictionCode: string): Promise<DocumentTemplate> {
    try {
      const response = await makeApiRequest<DocumentTemplate>(
        apiConfig.endpoints.documentGenerator.templates.removeJurisdiction(templateId, jurisdictionCode),
        { method: 'DELETE' },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error removing jurisdiction ${jurisdictionCode} from template ${templateId}:`, error);
      throw error;
    }
  }
}

// =============================================================================
// Document Service
// =============================================================================

class DocumentService {
  /**
   * Get all generated documents with optional filtering
   */
  async getAll(token: string, filters: DocumentFilters = {}): Promise<DocumentListResponse> {
    try {
      const params = new URLSearchParams();
      if (filters.status) {
        if (Array.isArray(filters.status)) {
          params.append('status', filters.status.join(','));
        } else {
          params.append('status', filters.status);
        }
      }
      if (filters.category) params.append('category', filters.category);
      if (filters.search) params.append('search', filters.search);
      if (filters.createdBy) params.append('createdBy', filters.createdBy);
      if (filters.templateId) params.append('templateId', filters.templateId);
      if (filters.fromDate) params.append('fromDate', filters.fromDate);
      if (filters.toDate) params.append('toDate', filters.toDate);
      if (filters.relatedContractId) params.append('relatedContractId', filters.relatedContractId);
      if (filters.jurisdiction) params.append('jurisdiction', filters.jurisdiction);
      if (filters.page) params.append('page', String(filters.page));
      if (filters.limit) params.append('limit', String(filters.limit));
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

      const queryString = params.toString();
      const url = queryString
        ? `${apiConfig.endpoints.documentGenerator.documents.getAll}?${queryString}`
        : apiConfig.endpoints.documentGenerator.documents.getAll;

      const response = await makeApiRequest<GeneratedDocument[]>(url, { method: 'GET' }, token);

      // The API returns { success: true, data: [...documents], pagination: {...} }
      const defaultPagination = { page: 1, limit: 10, total: 0, totalPages: 0, hasNextPage: false, hasPrevPage: false };
      const pag = response.pagination as any;
      return {
        data: response.data || [],
        pagination: pag ? {
          page: pag.page || 1,
          limit: pag.limit || 10,
          total: pag.total || 0,
          totalPages: pag.totalPages || pag.pages || 0,
          hasNextPage: pag.hasNextPage || false,
          hasPrevPage: pag.hasPrevPage || false
        } : defaultPagination
      };
    } catch (error) {
      console.error('Error fetching documents:', error);
      throw error;
    }
  }

  /**
   * Get single document by ID
   */
  async getById(token: string, id: string): Promise<GeneratedDocument> {
    try {
      const response = await makeApiRequest<GeneratedDocument>(
        apiConfig.endpoints.documentGenerator.documents.getOne(id),
        { method: 'GET' },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error fetching document ${id}:`, error);
      throw error;
    }
  }

  /**
   * Generate a new document from a template
   */
  async generate(token: string, input: GenerateDocumentInput): Promise<GeneratedDocument> {
    try {
      const response = await makeApiRequest<GeneratedDocument>(
        apiConfig.endpoints.documentGenerator.documents.generate,
        {
          method: 'POST',
          body: JSON.stringify(input)
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error('Error generating document:', error);
      throw error;
    }
  }

  /**
   * Update an existing document
   */
  async update(token: string, id: string, input: UpdateDocumentInput): Promise<GeneratedDocument> {
    try {
      const response = await makeApiRequest<GeneratedDocument>(
        apiConfig.endpoints.documentGenerator.documents.update(id),
        {
          method: 'PUT',
          body: JSON.stringify(input)
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error updating document ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a document
   */
  async delete(token: string, id: string): Promise<boolean> {
    try {
      await makeApiRequest(
        apiConfig.endpoints.documentGenerator.documents.delete(id),
        { method: 'DELETE' },
        token
      );
      return true;
    } catch (error) {
      console.error(`Error deleting document ${id}:`, error);
      throw error;
    }
  }

  /**
   * Preview document with placeholder values
   */
  async preview(token: string, templateId: string, filledValues: Record<string, any>, jurisdiction?: string): Promise<{ content: string }> {
    try {
      const response = await makeApiRequest<{ content: string }>(
        apiConfig.endpoints.documentGenerator.documents.preview(templateId),
        {
          method: 'POST',
          body: JSON.stringify({ filledValues, jurisdiction })
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error('Error previewing document:', error);
      throw error;
    }
  }

  /**
   * Download document as PDF
   */
  async downloadPdf(token: string, id: string): Promise<Blob> {
    try {
      const response = await fetch(
        `${apiConfig.baseURL}${apiConfig.endpoints.documentGenerator.documents.downloadPdf(id)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      return response.blob();
    } catch (error) {
      console.error(`Error downloading PDF for document ${id}:`, error);
      throw error;
    }
  }

  /**
   * Download document as DOCX
   */
  async downloadDocx(token: string, id: string): Promise<Blob> {
    try {
      const response = await fetch(
        `${apiConfig.baseURL}${apiConfig.endpoints.documentGenerator.documents.downloadDocx(id)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download DOCX');
      }

      return response.blob();
    } catch (error) {
      console.error(`Error downloading DOCX for document ${id}:`, error);
      throw error;
    }
  }

  /**
   * Add attachment to a document
   */
  async addAttachment(token: string, documentId: string, file: File): Promise<GeneratedDocument> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${apiConfig.baseURL}${apiConfig.endpoints.documentGenerator.documents.addAttachment(documentId)}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      if (!response.ok) {
        throw new Error('Failed to upload attachment');
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error adding attachment:', error);
      throw error;
    }
  }

  /**
   * Delete attachment from a document
   */
  async deleteAttachment(token: string, documentId: string, attachmentId: string): Promise<GeneratedDocument> {
    try {
      const response = await makeApiRequest<GeneratedDocument>(
        apiConfig.endpoints.documentGenerator.documents.deleteAttachment(documentId, attachmentId),
        { method: 'DELETE' },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error deleting attachment ${attachmentId}:`, error);
      throw error;
    }
  }

  /**
   * Compare two versions of a document
   */
  async compareVersions(token: string, documentId: string, version1: number, version2: number): Promise<VersionCompareResult> {
    try {
      const response = await makeApiRequest<VersionCompareResult>(
        `${apiConfig.endpoints.documentGenerator.documents.compareVersions(documentId)}?v1=${version1}&v2=${version2}`,
        { method: 'GET' },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error comparing versions for document ${documentId}:`, error);
      throw error;
    }
  }

  /**
   * Get specific version content
   */
  async getVersion(token: string, documentId: string, version: number): Promise<DocumentVersion> {
    try {
      const response = await makeApiRequest<DocumentVersion>(
        apiConfig.endpoints.documentGenerator.documents.getVersion(documentId, version),
        { method: 'GET' },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error fetching version ${version} for document ${documentId}:`, error);
      throw error;
    }
  }

  // =========================================================================
  // Approval Methods
  // =========================================================================

  /**
   * Submit document for approval
   */
  async submitForApproval(token: string, documentId: string, workflowId?: string): Promise<GeneratedDocument> {
    try {
      const response = await makeApiRequest<GeneratedDocument>(
        apiConfig.endpoints.documentGenerator.documents.submitForApproval(documentId),
        {
          method: 'POST',
          body: JSON.stringify({ workflowId })
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error submitting document ${documentId} for approval:`, error);
      throw error;
    }
  }

  /**
   * Process approval action (approve/reject/skip)
   */
  async processApprovalAction(token: string, documentId: string, input: ApprovalActionInput): Promise<GeneratedDocument> {
    try {
      const response = await makeApiRequest<GeneratedDocument>(
        apiConfig.endpoints.documentGenerator.documents.approvalAction(documentId),
        {
          method: 'POST',
          body: JSON.stringify(input)
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error processing approval action for document ${documentId}:`, error);
      throw error;
    }
  }

  /**
   * Get approval history for a document
   */
  async getApprovalHistory(token: string, documentId: string): Promise<any[]> {
    try {
      const response = await makeApiRequest<any[]>(
        apiConfig.endpoints.documentGenerator.documents.approvalHistory(documentId),
        { method: 'GET' },
        token
      );
      return response.data || [];
    } catch (error) {
      console.error(`Error fetching approval history for document ${documentId}:`, error);
      throw error;
    }
  }

  /**
   * Recall document from approval
   */
  async recall(token: string, documentId: string): Promise<GeneratedDocument> {
    try {
      const response = await makeApiRequest<GeneratedDocument>(
        apiConfig.endpoints.documentGenerator.documents.recall(documentId),
        { method: 'POST' },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error recalling document ${documentId}:`, error);
      throw error;
    }
  }

  /**
   * Sign a document
   */
  async sign(token: string, documentId: string, signatureData?: string): Promise<GeneratedDocument> {
    try {
      const response = await makeApiRequest<GeneratedDocument>(
        apiConfig.endpoints.documentGenerator.documents.sign(documentId),
        {
          method: 'POST',
          body: JSON.stringify({ signatureData })
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error signing document ${documentId}:`, error);
      throw error;
    }
  }
}

// =============================================================================
// Workflow Service
// =============================================================================

class WorkflowService {
  /**
   * Get all workflows with optional filtering
   */
  async getAll(token: string, filters: WorkflowFilters = {}): Promise<WorkflowListResponse> {
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.isActive !== undefined) params.append('isActive', String(filters.isActive));
      if (filters.category) params.append('category', filters.category);
      if (filters.department) params.append('department', filters.department);
      if (filters.page) params.append('page', String(filters.page));
      if (filters.limit) params.append('limit', String(filters.limit));

      const queryString = params.toString();
      const url = queryString
        ? `${apiConfig.endpoints.documentGenerator.workflows.getAll}?${queryString}`
        : apiConfig.endpoints.documentGenerator.workflows.getAll;

      const response = await makeApiRequest<ApprovalWorkflow[]>(url, { method: 'GET' }, token);

      // The API returns { success: true, data: [...workflows], pagination: {...} }
      const defaultPagination = { page: 1, limit: 10, total: 0, totalPages: 0, hasNextPage: false, hasPrevPage: false };
      const pag = response.pagination as any;
      return {
        data: response.data || [],
        pagination: pag ? {
          page: pag.page || 1,
          limit: pag.limit || 10,
          total: pag.total || 0,
          totalPages: pag.totalPages || pag.pages || 0,
          hasNextPage: pag.hasNextPage || false,
          hasPrevPage: pag.hasPrevPage || false
        } : defaultPagination
      };
    } catch (error) {
      console.error('Error fetching workflows:', error);
      throw error;
    }
  }

  /**
   * Get single workflow by ID
   */
  async getById(token: string, id: string): Promise<ApprovalWorkflow> {
    try {
      const response = await makeApiRequest<ApprovalWorkflow>(
        apiConfig.endpoints.documentGenerator.workflows.getOne(id),
        { method: 'GET' },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error fetching workflow ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new workflow
   */
  async create(token: string, input: CreateWorkflowInput): Promise<ApprovalWorkflow> {
    try {
      const response = await makeApiRequest<ApprovalWorkflow>(
        apiConfig.endpoints.documentGenerator.workflows.create,
        {
          method: 'POST',
          body: JSON.stringify(input)
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error('Error creating workflow:', error);
      throw error;
    }
  }

  /**
   * Update an existing workflow
   */
  async update(token: string, id: string, input: Partial<CreateWorkflowInput>): Promise<ApprovalWorkflow> {
    try {
      const response = await makeApiRequest<ApprovalWorkflow>(
        apiConfig.endpoints.documentGenerator.workflows.update(id),
        {
          method: 'PUT',
          body: JSON.stringify(input)
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error(`Error updating workflow ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a workflow
   */
  async delete(token: string, id: string): Promise<boolean> {
    try {
      await makeApiRequest(
        apiConfig.endpoints.documentGenerator.workflows.delete(id),
        { method: 'DELETE' },
        token
      );
      return true;
    } catch (error) {
      console.error(`Error deleting workflow ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get pending approvals for current user
   */
  async getPendingApprovals(token: string, page = 1, limit = 10): Promise<PendingApprovalsResponse> {
    try {
      const response = await makeApiRequest<PendingApproval[]>(
        `${apiConfig.endpoints.documentGenerator.pendingApprovals}?page=${page}&limit=${limit}`,
        { method: 'GET' },
        token
      );

      // The API returns { success: true, data: [...approvals], pagination: {...} }
      const defaultPagination = { page: 1, limit: 10, total: 0, totalPages: 0, hasNextPage: false, hasPrevPage: false };
      const pag = response.pagination as any;
      return {
        data: response.data || [],
        pagination: pag ? {
          page: pag.page || 1,
          limit: pag.limit || 10,
          total: pag.total || 0,
          totalPages: pag.totalPages || pag.pages || 0,
          hasNextPage: pag.hasNextPage || false,
          hasPrevPage: pag.hasPrevPage || false
        } : defaultPagination
      };
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      throw error;
    }
  }
}

// =============================================================================
// AI Service
// =============================================================================

class DocumentAIService {
  /**
   * Check if AI service is available
   */
  async getStatus(token: string): Promise<{ available: boolean; message: string }> {
    try {
      const response = await makeApiRequest<{ available: boolean; message: string }>(
        apiConfig.endpoints.documentGenerator.ai.status,
        { method: 'GET' },
        token
      );
      return response.data!;
    } catch (error) {
      console.error('Error checking AI status:', error);
      return { available: false, message: 'Failed to check AI status' };
    }
  }

  /**
   * Improve content based on mode
   */
  async improveContent(
    token: string,
    content: string,
    mode: AIImprovementMode,
    context?: string
  ): Promise<AIImprovementResult> {
    try {
      const response = await makeApiRequest<AIImprovementResult>(
        apiConfig.endpoints.documentGenerator.ai.improve,
        {
          method: 'POST',
          body: JSON.stringify({ content, mode, context })
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error('Error improving content:', error);
      throw error;
    }
  }

  /**
   * Get alternative phrasing suggestions
   */
  async suggestAlternatives(
    token: string,
    text: string,
    count = 3
  ): Promise<AlternativeSuggestion[]> {
    try {
      const response = await makeApiRequest<{ alternatives: AlternativeSuggestion[] }>(
        apiConfig.endpoints.documentGenerator.ai.alternatives,
        {
          method: 'POST',
          body: JSON.stringify({ text, count })
        },
        token
      );
      return response.data?.alternatives || [];
    } catch (error) {
      console.error('Error getting alternatives:', error);
      throw error;
    }
  }

  /**
   * Check document for compliance issues
   */
  async checkCompliance(
    token: string,
    content: string,
    context: {
      documentType: string;
      jurisdiction: string;
      industry?: string;
      regulations?: string[];
    }
  ): Promise<ComplianceCheckResult> {
    try {
      const response = await makeApiRequest<ComplianceCheckResult>(
        apiConfig.endpoints.documentGenerator.ai.compliance,
        {
          method: 'POST',
          body: JSON.stringify({ content, ...context })
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error('Error checking compliance:', error);
      throw error;
    }
  }

  /**
   * Generate a clause based on requirements
   */
  async generateClause(
    token: string,
    requirements: {
      type: string;
      purpose: string;
      jurisdiction: string;
      specificRequirements?: string;
      tone?: string;
    }
  ): Promise<ClauseGenerationResult> {
    try {
      const response = await makeApiRequest<ClauseGenerationResult>(
        apiConfig.endpoints.documentGenerator.ai.generateClause,
        {
          method: 'POST',
          body: JSON.stringify(requirements)
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error('Error generating clause:', error);
      throw error;
    }
  }

  /**
   * Simplify legal language
   */
  async simplifyLanguage(
    token: string,
    text: string,
    targetAudience: 'general' | 'business' | 'technical' = 'general'
  ): Promise<SimplifyResult> {
    try {
      const response = await makeApiRequest<SimplifyResult>(
        apiConfig.endpoints.documentGenerator.ai.simplify,
        {
          method: 'POST',
          body: JSON.stringify({ text, targetAudience })
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error('Error simplifying language:', error);
      throw error;
    }
  }

  /**
   * Summarize changes between document versions
   */
  async summarizeChanges(
    token: string,
    originalContent: string,
    newContent: string
  ): Promise<ChangeSummaryResult> {
    try {
      const response = await makeApiRequest<ChangeSummaryResult>(
        apiConfig.endpoints.documentGenerator.ai.summarizeChanges,
        {
          method: 'POST',
          body: JSON.stringify({ originalContent, newContent })
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error('Error summarizing changes:', error);
      throw error;
    }
  }

  /**
   * Analyze placeholders in template content
   */
  async analyzePlaceholders(token: string, content: string): Promise<PlaceholderAnalysisResult> {
    try {
      const response = await makeApiRequest<PlaceholderAnalysisResult>(
        apiConfig.endpoints.documentGenerator.ai.analyzePlaceholders,
        {
          method: 'POST',
          body: JSON.stringify({ content })
        },
        token
      );
      return response.data!;
    } catch (error) {
      console.error('Error analyzing placeholders:', error);
      throw error;
    }
  }
}

// =============================================================================
// Jurisdiction Service
// =============================================================================

class JurisdictionService {
  /**
   * Get all supported jurisdictions
   */
  async getAll(token: string): Promise<Jurisdiction[]> {
    try {
      const response = await makeApiRequest<Jurisdiction[]>(
        apiConfig.endpoints.documentGenerator.jurisdictions,
        { method: 'GET' },
        token
      );
      return response.data || [];
    } catch (error) {
      console.error('Error fetching jurisdictions:', error);
      throw error;
    }
  }
}

// =============================================================================
// Export Services
// =============================================================================

export const templateService = new TemplateService();
export const documentService = new DocumentService();
export const workflowService = new WorkflowService();
export const documentAIService = new DocumentAIService();
export const jurisdictionService = new JurisdictionService();

// Combined service for convenience
export const documentGeneratorService = {
  templates: templateService,
  documents: documentService,
  workflows: workflowService,
  ai: documentAIService,
  jurisdictions: jurisdictionService
};

export default documentGeneratorService;
